<template>
  <div class="login-wrapper">
    <!-- 背景图片 -->
    <div class="login-background">
      <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920&q=80" alt="background">
      <div class="background-overlay"></div>
    </div>

    <!-- 颗粒感纹理 -->
    <div class="grain-overlay"></div>

    <!-- 主内容区域 -->
    <div class="login-main-content">
      <!-- 左侧品牌区域 -->
      <div class="brand-section">
        <span class="brand-tag">物业管理门户</span>
        <h1 class="brand-title">
          数字化<br>
          <span class="brand-title-italic">避风港</span>
        </h1>
        <p class="brand-description">
          专为高端建筑组合之敏锐管理而设计的精细化后端环境。
        </p>
      </div>

      <!-- 右侧登录卡片 -->
      <div class="login-card">
        <!-- Logo -->
    

        <!-- 表单标题 -->
        <div class="login-header">
          <h2 class="login-system-title">意禾物业管理系统</h2>
          <div class="login-divider"></div>
        </div>

        <!-- 登录表单 -->
        <el-form :model="loginForm" :rules="rules" ref="loginForm" class="login-form">
          <el-form-item prop="username">
            <div class="form-field">
              <label class="form-label">用户名</label>
              <el-input
                v-model="loginForm.username"
                :placeholder="$t('login.usernamePlaceholder')"
                prefix-icon="el-icon-user"
                class="custom-input"
              ></el-input>
            </div>
            <div class="remember-account" v-if="loginForm.username">
              <el-checkbox v-model="rememberAccount">记住账号</el-checkbox>
            </div>
          </el-form-item>

          <el-form-item prop="passwd">
            <div class="form-field">
              <label class="form-label">密码</label>
              <el-input 
                v-model="loginForm.passwd" 
                type="password" 
                :placeholder="$t('login.passwordPlaceholder')"
                prefix-icon="el-icon-lock"
                class="custom-input"
              ></el-input>
            </div>
          </el-form-item>

          <el-form-item class="login-button-item">
            <el-button 
              type="primary" 
              @click="submitForm('loginForm')" 
              :loading="loading"
              class="login-button"
            >
              <span>登录系统</span>
              <i class="el-icon-arrow-right"></i>
            </el-button>
          </el-form-item>

          <div class="security-info">
            <span>仅限授权人员访问</span>
            <span class="dot"></span>
            <span>安全等级：一级</span>
          </div>
        </el-form>
      </div>
    </div>

    <!-- 底部信息 -->
    <footer class="login-footer">
      <div class="footer-copyright">©2020-2026 <span v-html="companyName"></span></div>
      <div class="footer-links">
        <a href="#">隐私条款</a>
        <a href="#">服务协议</a>
        <a href="#">技术支持</a>
      </div>
    </footer>

    <!-- 选择用户弹窗 -->
    <select-login-user ref="selectLoginUserRef" @select="handleSelect"></select-login-user>
  </div>
</template>

<script>
import { login, _loadStaffPrivileges } from '@/api/user/loginApi';
import { _loadCommunityInfo } from '@/api/community/communityApi';
import { listSystemInfo } from '@/api/system/systemInfoManageApi';
import selectLoginUser from '@/components/user/selectLoginUser.vue';

export default {
  name: 'Login',
  data() {
    return {
      logo: '',
      companyName: '',
      rememberAccount: true,
      loginForm: {
        username: '',
        passwd: ''
      },
      loading: false,
      rules: {
        username: [
          { required: true, message: this.$t('login.usernamePlaceholder'), trigger: 'blur' }
        ],
        passwd: [
          { required: true, message: this.$t('login.passwordPlaceholder'), trigger: 'blur' }
        ]
      }
    };
  },
  created() {
    this.clearLoginState();
    this.restoreRememberedUsername();
    this.getSystemInfo();
  },
  components: {
    selectLoginUser
  },
  methods: {
    // 仅清理登录态相关数据，保留系统信息、社区选择、语言等本地配置
    clearLoginState() {
      const loginStateKeys = [
        'token',
        'user',
        'userInfo',
        'hc_staff_privilege',
        'hc_communityInfos',
        'hc_currentCommunityInfo'
      ];
      loginStateKeys.forEach((key) => {
        localStorage.removeItem(key);
      });
    },
    // 仅恢复上次记住的账号，密码始终不持久化
    restoreRememberedUsername() {
      try {
        const rememberedUsername = localStorage.getItem('yihe_remembered_username');
        if (rememberedUsername) {
          this.loginForm.username = rememberedUsername;
          this.rememberAccount = true;
        }
      } catch (e) {
        // 读取异常时忽略，避免影响登录
      }
    },
    saveRememberedUsername(username) {
      try {
        localStorage.setItem('yihe_remembered_username', username);
      } catch (e) {
        // 写入异常时忽略，避免影响登录
      }
    },
    async getSystemInfo() {
      const { data } = await listSystemInfo({ page: 1, row: 1 });
      this.logo = data[0].logoUrl;
      this.companyName = data[0].companyName;
    },
    async submitForm(formName) {
      this.$refs[formName].validate(async (valid) => {
        if (valid) {
          this.loading = true;
          try {
            const { data } = await login(this.loginForm).catch(error => {
              this.$message.error(error);
              return { data: [] };
            });
            if (!data || data.length == 0) {
              return;
            }

            if (data && data.length > 1) {
              this.$refs.selectLoginUserRef.openDialog(data);
              return;
            }
            this.handleSelect(data[0]);
          } catch (error) {
            console.error('登录失败：', error);
          } finally {
            this.loading = false;
          }
        } else {
          return false;
        }
      });
    },
    async handleSelect(_userData) {
      // 登录成功后再记录账号；用户取消“记住账号”时立即清掉上次记录
      if (this.rememberAccount && this.loginForm.username) {
        this.saveRememberedUsername(this.loginForm.username);
      } else {
        localStorage.removeItem('yihe_remembered_username');
      }
      localStorage.setItem('token', _userData.token);
      let _user = {
        userId: _userData.userId,
        name: _userData.name,
        tel: _userData.tel
      };
      localStorage.setItem('user', JSON.stringify(_user));
      await _loadStaffPrivileges();
      await _loadCommunityInfo();
      this.$router.push('/');
    }
  }
};
</script>

<style scoped>
/* 引入字体 */
@import url('https://fonts.googleapis.com/css2?family=Noto+Serif+SC:wght@200..900&family=Manrope:wght@200..800&display=swap');

/* 全局容器 */
.login-wrapper {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  font-family: 'Manrope', 'Noto Serif SC', sans-serif;
}

/* 背景图片 */
.login-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.login-background img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: grayscale(20%) brightness(0.85);
}

.background-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(0, 52, 52, 0.2) 0%, rgba(0, 77, 77, 0.15) 100%);
}

/* 颗粒感纹理 */
.grain-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  opacity: 0.03;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
}

/* 主内容区域 */
.login-main-content {
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 80px;
  height: calc(100vh - 80px);
  padding: 0 60px;
}

/* 左侧品牌区域 */
.brand-section {
  display: none;
  flex-direction: column;
  max-width: 400px;
  color: #ffffff;
}

@media (min-width: 1024px) {
  .brand-section {
    display: flex;
  }
}

.brand-tag {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3em;
  margin-bottom: 20px;
  opacity: 0.7;
  font-weight: 500;
}

.brand-title {
  font-family: 'Noto Serif SC', serif;
  font-size: 56px;
  font-weight: 300;
  line-height: 1.1;
  letter-spacing: -0.02em;
  margin-bottom: 30px;
}

.brand-title-italic {
  font-style: italic;
  color: #b0eeed;
}

.brand-description {
  font-size: 14px;
  line-height: 1.8;
  opacity: 0.8;
  max-width: 320px;
  border-left: 2px solid #80bdbc;
  padding-left: 24px;
}

/* 登录卡片 */
.login-card {
  width: 100%;
  max-width: 420px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(20px);
  padding: 50px 40px;
  box-shadow: 0 32px 64px -12px rgba(26, 28, 28, 0.08);
  border-left: 1px solid rgba(255, 255, 255, 0.5);
  border-top: 1px solid rgba(255, 255, 255, 0.5);
}

/* Logo */
.login-logo {
  text-align: center;
  margin-bottom: 20px;
}

.login-logo img {
  max-height: 60px;
  max-width: 100%;
}

/* 表单标题 */
.login-header {
  margin-bottom: 35px;
}

.login-system-title {
  font-family: 'Noto Serif SC', serif;
  font-size: 24px;
  font-weight: 400;
  color: #003434;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 12px;
}

.login-divider {
  width: 48px;
  height: 2px;
  background: #735c00;
}

/* 表单样式 */
.login-form {
  margin-top: 20px;
}

.form-field {
  width: 100%;
}

.form-label {
  display: block;
  font-family: 'Noto Serif SC', serif;
  font-size: 13px;
  color: #003434;
  font-style: italic;
  margin-bottom: 8px;
}

.form-label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.forgot-link {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #6f7978;
  text-decoration: none;
  transition: color 0.3s;
}

.forgot-link:hover {
  color: #735c00;
}

/* 自定义输入框 */
.custom-input {
  width: 100%;
}

.custom-input >>> .el-input__inner {
  background: transparent;
  border: none;
  border-bottom: 1px solid #bfc8c8;
  border-radius: 0;
  padding: 12px 0;
  font-size: 14px;
  color: #1a1c1c;
  transition: border-color 0.5s;
}

.custom-input >>> .el-input__inner:focus {
  border-bottom-color: #735c00;
  outline: none;
}

.custom-input >>> .el-input__inner::placeholder {
  color: #bfc8c8;
  font-weight: 300;
}

.custom-input >>> .el-input__prefix {
  color: #bfc8c8;
  font-size: 16px;
}

/* 登录按钮 */
.login-button-item {
  margin-top: 30px;
  margin-bottom: 0;
}

.login-button {
  width: 100%;
  height: 52px;
  background: linear-gradient(135deg, #003434 0%, #004d4d 100%);
  border: none;
  border-radius: 0;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.5s;
}

.login-button:hover {
  background: linear-gradient(135deg, #004d4d 0%, #006666 100%);
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(0, 52, 52, 0.3);
}

.login-button >>> .el-icon-arrow-right {
  transition: transform 0.3s;
}

.login-button:hover >>> .el-icon-arrow-right {
  transform: translateX(4px);
}

/* 安全信息 */
.security-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #bfc8c8;
}

.security-info .dot {
  width: 4px;
  height: 4px;
  background: #735c00;
  border-radius: 50%;
}

/* Element UI 表单项覆盖 */
.login-form >>> .el-form-item {
  margin-bottom: 24px;
}

.login-form >>> .el-form-item__error {
  font-size: 11px;
  padding-top: 4px;
  color: #ba1a1a;
}

/* 记住账号复选框 */
.remember-account {
  margin-top: 8px;
  font-size: 12px;
  color: #6f7978;
  display: flex;
  justify-content: flex-end;
}

.remember-account >>> .el-checkbox__label {
  font-size: 12px;
  color: #6f7978;
  letter-spacing: 0.05em;
}

/* 底部信息 */
.login-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 24px 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 20;
  box-sizing: border-box;
}

.footer-copyright {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: rgba(255, 255, 255, 0.4);
}

.footer-links {
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
}

.footer-links a {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: rgba(255, 255, 255, 0.4);
  text-decoration: none;
  transition: color 0.3s;
}

.footer-links a:hover {
  color: #ffe088;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .login-main-content {
    padding: 0 20px;
    gap: 40px;
  }

  .login-card {
    padding: 40px 30px;
    max-width: 100%;
  }

  .login-footer {
    flex-direction: column;
    gap: 15px;
    padding: 20px;
    text-align: center;
  }

  .footer-links {
    gap: 20px;
    justify-content: center;
  }

  .footer-links a {
    font-size: 9px;
  }
}
</style>
